import React, { useState, useEffect } from 'react';
import { Field, Label, Textarea } from '@headlessui/react';
import clsx from 'clsx';

const Step3 = ({ next, back, data, files, setFiles }) => {
  const [formData, setFormData] = useState(data);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [wordCount, setWordCount] = useState({ engagementDescription: 0, engagementWhy: 0, howDidYouHear: 0, additionalNotes: 0 });

  useEffect(() => {
    setWordCount({
      engagementDescription: countWords(formData.engagementDescription || ''),
      engagementWhy: countWords(formData.engagementWhy || ''),
      howDidYouHear: countWords(formData.howDidYouHear || ''),
      additionalNotes: countWords(formData.additionalNotes || ''),
    });
  }, [formData]);

  const countWords = (text) => {
    return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const words = countWords(value);
    if (words <= 300) {
      setFormData({ ...formData, [name]: value });
      setWordCount({ ...wordCount, [name]: words });
    }

    if (errors[name]) {
      setErrors((prevErrors) => {
        const { [name]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0) + files.reduce((acc, file) => acc + file.size, 0);

    if (selectedFiles.some(file => file.type !== 'application/pdf')) {
      setFileError('Nur PDF-Dateien sind erlaubt!');
    } else if (totalSize > 15 * 1024 * 1024) {
      setFileError('Die gesamte Dateigröße darf 15 MB nicht überschreiten!');
    } else {
      setFileError('');
      setFiles([...files, ...selectedFiles]);
    }
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.engagementDescription || wordCount.engagementDescription < 80) newErrors.engagementDescription = 'Diese Beschreibung muss zwischen 80 und 300 Wörtern enthalten';
    if (!formData.engagementWhy || wordCount.engagementWhy < 80) newErrors.engagementWhy = 'Diese Beschreibung muss zwischen 80 und 300 Wörtern enthalten';
    if (formData.howDidYouHear && wordCount.howDidYouHear > 300) newErrors.howDidYouHear = 'Diese Beschreibung darf nicht mehr als 300 Wörter enthalten!';
    if (formData.additionalNotes && wordCount.additionalNotes > 300) newErrors.additionalNotes = 'Diese Beschreibung darf nicht mehr als 300 Wörter enthalten!';
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      next(formData, files);
    }
  };

  return (
    <div className="step text-white flex flex-col gap-12">
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl font-bold">Engagement-Details</h2>

        <div className="space-y-4">

          <Field className="relative">
            <p className="font-semibold max-w-lg text-lg/6 pb-2 mt-4">Wie engagiert sich die nominierte Person? Was tut sie konkret? Welches Ziel verfolgt sie mit ihrem Einsatz? Welche Veränderung möchte sie bewirken?</p>
            <Textarea
              placeholder="Min. 80, max. 300 Wörter"
              name="engagementDescription"
              value={formData.engagementDescription}
              onChange={handleChange}
              rows={6}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
            <p className="text-white/80 text-xs/5 px-6 absolute right-0 -mt-8">{wordCount.engagementDescription}/300 Wörter</p>
            {errors.engagementDescription && <p className="error px-6">{errors.engagementDescription}</p>}
          </Field>

          <Field className="relative">
          <p className="font-semibold max-w-lg text-lg/6 pb-2 mt-4">Warum denkst du, sollte deine nominierte Person Teil der Movers of Tomorrow werden? Bitte erkläre, was die nominierte Person zu einem Vorbild für andere junge Menschen macht, wieviel Eigeninitiative in dem Engagement steckt und wieviel Mut er/sie für das Engagement aufbringt.</p>
            <Textarea
              placeholder="Min. 80, max. 300 Wörter"
              name="engagementWhy"
              value={formData.engagementWhy}
              onChange={handleChange}
              rows={6}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
            <p className="text-white/80 text-xs/5 px-6 absolute right-0 -mt-8">{wordCount.engagementWhy}/300 Wörter</p>
            {errors.engagementWhy && <p className="error px-6">{errors.engagementWhy}</p>}
          </Field>

          <Field className="col-span-full pt-12 space-y-4">
            <Label><h2 className="text-xl/7 font-bold">Unterlagen zur Unterstützung (optional)</h2></Label>
            <p className="text-white/80 text-sm/5">Lade alle unterstützenden Dokumente, Fotos oder Videos hoch, die das Engagement der nominierten Person zeigen (z.B. Projektberichte, Testimonials, Medienberichte). Maximalgesamtgröße: 15 MB.</p>
            <div className="mt-2 flex justify-center bg-white/5 px-6 py-12 pb-12 relative">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-8 mx-auto text-white/80">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                </svg>

                <div className="mt-4 flex text-sm/6 text-white/80">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer px-4 py-1 pb-1.5 rounded-md bg-purple font-semibold text-sm/5 text-white/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-purple focus-within:ring-offset-2 hover:ring-black/10 hover:bg-purple/90 transition duration-100"
                  >
                    <span>PDFs hochladen</span>
                    <input id="file-upload" name="files[]" type="file" accept=".pdf" multiple onChange={handleFileChange} className="sr-only" />
                  </label>
                  <p className="pl-4 text-sm/5 py-1">oder drag und drop</p>
                </div>
                <p className="text-xs leading-7 text-white/50">Maximalgröße aller Assets: 15MB</p>
                {fileError && <p className="error">{fileError}</p>}
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <p className="text-white text-sm">{file.name}</p>
                        <button
                          className="ml-2 text-white/80 hover:text-purple focus:outline-none"
                          onClick={() => removeFile(index)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Field>

          <div className="pt-12 space-y-4">
            <h2 className="text-xl/7 font-bold pr-6 md:pr-32">Zusätzliche Informationen</h2>
          </div>

          <Field className="relative">
            <p className="font-semibold max-w-lg text-lg/6 pb-2 mt-8">Wie hast du von dem Engagement der nominierten Person erfahren? <span className="text-sm text-white/80 italic">(Optional)</span></p>
            <Textarea
              placeholder="Optional. Max. 300 Wörter"
              name="howDidYouHear"
              value={formData.howDidYouHear}
              onChange={handleChange}
              rows={5}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
            <p className="text-white/80 text-xs/5 px-6 absolute right-0 -mt-8">{wordCount.howDidYouHear}/300 Wörter</p>
            {errors.howDidYouHear && <p className="error px-6">{errors.howDidYouHear}</p>}
          </Field>

          <Field className="relative">
            <p className="font-semibold max-w-lg text-lg/6 pb-2 mt-8">Gibt es sonst noch etwas, das du zum Engagement oder zu der nominierten Person hinzufügen möchtest? <span className="text-sm text-white/80 italic">(Optional)</span></p>
            <Textarea
              placeholder="Optional. Max. 300 Wörter"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleChange}
              rows={5}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
            <p className="text-white/80 text-xs/5 px-6 absolute right-0 -mt-8">{wordCount.additionalNotes}/300 Wörter</p>
            {errors.additionalNotes && <p className="error px-6">{errors.additionalNotes}</p>}
          </Field>

        </div>
      </div>

      {/* Progress Navigation */}
      <nav className="progress-nav">
        <div className="inner flex h-full items-center justify-between">
          <button onClick={back} className="flex gap-3 items-center hover:text-purple">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
            </svg>
            <span className="block -mt-1 font-semibold tracking-wide">Zurück</span>
          </button>
          <button onClick={handleSubmit} className="primary-form">
            <span className="font-semibold tracking-wide -mt-0.5 block text-lg">Weiter</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Step3;
